import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import moment from 'moment-timezone';

import {
  IAddress,
  IAutoQuoteEnrollment,
  IAutoEnrollmentApplication,
  IAutoQuote,
  IAutoThirdPartyDataOrders,
} from '../../interfaces';
import getTimezone from '../../services/getTimezone';
import updateValueAtPath from '../../services/updateValueAtPath';

type TEnrollmentApplicationHelper = {
  enrollmentApplication: IAutoEnrollmentApplication;
  quote?: IAutoQuote;
  verticalCode?: string;
  partnerCode?: string;
};

function updateEnrollmentApplication(
  updatedValues: Partial<IAutoEnrollmentApplication>,
  enrollmentApplication: IAutoEnrollmentApplication
) {
  return updateValueAtPath({
    base: enrollmentApplication,
    updatedValues,
  });
}

function updateClientNameDob(
  {
    dob,
    firstName,
    lastName,
  }: {
    dob: string;
    firstName: string;
    lastName: string;
  },
  enrollmentApplication: IAutoEnrollmentApplication
) {
  const formattedDob = moment.utc(dob, 'MM/DD/YYYY').format('YYYY-MM-DD');
  const updatedApplication = updateValueAtPath({
    updatedValues: {
      firstName,
      lastName,
      dob: formattedDob,
    },
    base: enrollmentApplication || {},
  });
  return updatedApplication;
}

function updateHomeAddress(
  address: IAddress,
  { hasMoved }: { hasMoved?: boolean },
  enrollmentApplication: IAutoEnrollmentApplication
) {
  return getTimezone(address.latitude!, address.longitude!).then((tzData) => {
    const addressWithTimezone = {
      ...address,
      timezone: tzData.timeZoneId,
    };
    const updatedApplication = hasMoved
      ? updateValueAtPath({
          updatedValues: { previous_address: addressWithTimezone },
          base: enrollmentApplication,
        })
      : updateValueAtPath({
          updatedValues: { address: addressWithTimezone },
          base: enrollmentApplication,
        });
    return updatedApplication;
  });
}

function getQuoteFromEnrollmentApplication({
  enrollmentApplication,
  verticalCode = 'toggle_auto',
  partnerCode,
}: TEnrollmentApplicationHelper) {
  const {
    firstName: first_name,
    lastName: last_name,
    dob,
    address,
    selfReportedIncidentCount: self_reported_incident_count = 0,
    coverageEffectiveDate: policy_effective_date,
    email,
    phoneNumber: phone_number,
  } = enrollmentApplication;
  const sanitizedAddress = {
    ...omit(address, ['fullAddress']),
  } as IAddress;
  const emailPhoneNumber =
    !!email && !!phone_number
      ? {
          email,
          phone_number,
        }
      : {};

  const holderAddress = omit(sanitizedAddress, ['timezone']);

  const payload: IAutoQuoteEnrollment = {
    vertical_code: verticalCode,
    ...(partnerCode ? { partner_code: partnerCode } : {}),
    application: {
      billing: {
        billing_type: 'platform_eft',
        billing_cadence: 'monthly',
      },
      client: {
        first_name,
        last_name,
        dob,
        address: holderAddress,
        ...emailPhoneNumber,
      },
      self_reported_incident_count,
      policy_effective_date,
      drivers: [
        {
          first_name,
          last_name,
          dob,
          role: 'pni',
          relationship_to_insured: 'self',
        },
      ],
      holders: [
        {
          first_name,
          last_name,
          dob,
          address: holderAddress,
          position_on_policy: 1,
        },
      ],
      rating_address: sanitizedAddress,
    },
  };

  return payload;
}

function getUpdatedQuoteFromEnrollmentApplication({
  partnerCode,
  verticalCode = 'toggle_auto',
  enrollmentApplication,
  quote,
}: TEnrollmentApplicationHelper) {
  const quoteWithNewValues = getQuoteFromEnrollmentApplication({
    enrollmentApplication,
    verticalCode,
    partnerCode,
  });

  const { email, phoneNumber: phone_number } = enrollmentApplication;

  const previousAddress = enrollmentApplication.previous_address;

  const { drivers: driversList = [], holders: holdersList = [] } =
    quote?.application || {};

  const pniDriver = driversList.find(({ role }) => role === 'pni');
  const primaryHolder = holdersList.find(
    (holder) => holder.position_on_policy === 1
  );

  const updatedDriver = {
    ...pniDriver,
    ...quoteWithNewValues.application.drivers[0],
  };

  const updatedDrivers = [
    ...driversList.filter((driver) => driver !== pniDriver),
    updatedDriver,
  ];

  const updatedHolder = {
    ...primaryHolder,
    ...quoteWithNewValues.application.holders[0],
  };

  const updatedHolders = [
    ...holdersList.filter((holder) => holder !== primaryHolder),
    updatedHolder,
  ];

  const emailPhoneNumber =
    !!email && !!phone_number
      ? {
          email,
          phone_number,
        }
      : {};

  const updatedQuote: IAutoQuoteEnrollment = {
    ...quote,
    ...quoteWithNewValues,
    application: {
      ...(quote?.application || {}),
      ...quoteWithNewValues.application,
      drivers: updatedDrivers,
      holders: updatedHolders,
      metadata: {
        discounts: [],
        job_requests: [],
        suggested_drivers: [],
        suggested_vehicles: [],
        third_party_data_orders: [],
        removed_vehicles_to_display: [],
        requires_no_prior_questionnaire: false,
        ...quote?.application.metadata,
        ...(previousAddress ? { previous_address: previousAddress } : {}),
      },
      client: {
        ...(quote?.application.client || {}),
        ...(quoteWithNewValues.application.client || {}),
        ...emailPhoneNumber,
      },
    },
  };

  return updatedQuote;
}

function addReadyAdpfOrder(quote: IAutoQuoteEnrollment) {
  const orderPath = 'application.metadata.third_party_data_orders';
  const currentThirdPartyData = get(quote, orderPath, []);
  if (
    find(
      currentThirdPartyData,
      ({ data_type, status }: IAutoThirdPartyDataOrders = {}) =>
        data_type === 'adpf' && (status === 'ready' || status === 'pending')
    )
  ) {
    return quote;
  }
  const updatedThirdPartyDataOrders = [
    ...currentThirdPartyData,
    {
      data_type: 'adpf',
      status: 'ready',
    },
  ];
  return updateValueAtPath({
    base: quote,
    path: orderPath,
    updatedValues: updatedThirdPartyDataOrders,
  });
}

function getAreAdpfErrorsHandled(
  valuesThatTriggerAdpfError: {
    dob?: string;
    firstName?: string;
    lastName?: string;
    address?: IAddress;
  },
  updatedValues: {
    dob?: string;
    firstName?: string;
    lastName?: string;
    address?: IAddress;
  }
) {
  const truthyErrorValues = pickBy(valuesThatTriggerAdpfError);
  const valuesOfErrorInputs = mapValues(
    truthyErrorValues,
    (_, key: 'dob' | 'firstName' | 'lastName' | 'address') =>
      key === 'dob' && !!updatedValues.dob
        ? moment.utc(updatedValues.dob).format('YYYY-MM-DD')
        : updatedValues[key]
  );

  return !isEqual(truthyErrorValues, valuesOfErrorInputs);
}

export function getEnrollmentApplicationHelpers(
  options: TEnrollmentApplicationHelper
) {
  const { enrollmentApplication } = options;

  return {
    addReadyAdpfOrder,
    getAreAdpfErrorsHandled,
    getQuoteFromEnrollmentApplication: () =>
      getQuoteFromEnrollmentApplication(options),
    getUpdatedApplication: (
      updatedValues: Partial<IAutoEnrollmentApplication>
    ) => updateEnrollmentApplication(updatedValues, enrollmentApplication),
    getUpdatedQuoteFromEnrollmentApplication: () =>
      getUpdatedQuoteFromEnrollmentApplication(options),
    updateClientNameDob: (values: {
      dob: string;
      firstName: string;
      lastName: string;
    }) => updateClientNameDob(values, enrollmentApplication),
    updateHomeAddress: (
      address: IAddress,
      opts: { hasMoved?: boolean | undefined }
    ) => updateHomeAddress(address, opts, enrollmentApplication),
  };
}
